import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Car, Mail, ArrowLeft, Send } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Toaster } from '../../components/ui/sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ForgotPasswordPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Veuillez entrer votre adresse email');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/admin/forgot-password`, { email });
      setSubmitted(true);
      toast.success('Email envoyé !');
    } catch (error) {
      console.error('Error:', error);
      // Show success anyway to prevent email enumeration
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center p-4">
        <Toaster position="top-right" richColors />
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          <div className="glass-surface p-8 space-y-6 text-center">
            <div className="w-16 h-16 bg-primary mx-auto flex items-center justify-center mb-4 rounded-full">
              <Mail className="w-8 h-8 text-[#181B1F]" strokeWidth={1.5} />
            </div>
            
            <h1 className="font-serif text-2xl text-[#181B1F] font-light">
              Vérifiez votre boîte mail
            </h1>
            
            <p className="text-gray-500">
              Si un compte existe avec l'adresse <strong>{email}</strong>, vous recevrez un email avec les instructions pour réinitialiser votre mot de passe.
            </p>
            
            <p className="text-gray-400 text-sm">
              Le lien expire dans 1 heure.
            </p>
            
            <div className="pt-4">
              <Button
                onClick={() => navigate('/admin')}
                variant="outline"
                className="w-full"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Retour à la connexion
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center p-4">
      <Toaster position="top-right" richColors />
      
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="glass-surface p-8 space-y-8">
          {/* Logo */}
          <div className="text-center">
            <div className="w-16 h-16 bg-primary mx-auto flex items-center justify-center mb-4">
              <Car className="w-8 h-8 text-[#181B1F]" strokeWidth={1.5} />
            </div>
            <h1 className="font-serif text-2xl text-[#181B1F] font-light">
              Mot de passe oublié ?
            </h1>
            <p className="text-gray-500 text-sm mt-2">
              Entrez votre email pour recevoir un lien de réinitialisation
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-gray-500">Adresse email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input-default pl-10"
                  placeholder="admin@example.com"
                  data-testid="forgot-password-email"
                />
              </div>
            </div>

            <Button 
              type="submit" 
              disabled={loading}
              className="w-full btn-primary"
              data-testid="forgot-password-submit"
            >
              {loading ? 'Envoi en cours...' : 'Envoyer le lien'}
              <Send className="w-4 h-4 ml-2" />
            </Button>
          </form>
        </div>

        {/* Back to login */}
        <div className="text-center mt-6">
          <Link
            to="/admin"
            className="text-gray-400 hover:text-[#181B1F] text-sm transition-colors inline-flex items-center"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Retour à la connexion
          </Link>
        </div>
      </motion.div>
    </div>
  );
};

export default ForgotPasswordPage;
